import { DocumentsListBrokerageFirmModule, IntermediaryInformationCardModule } from '@korint/folio';
import type { IntermediaryInformationCardModuleField } from '@folio/modules/IntermediaryInformationCardModule/IntermediaryInformationCardModule';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  configControllerGetPublicTenantConfigQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
} from 'korint-sdk';
import { useTranslation } from 'react-i18next';
import { Stack } from '@folio/components/ui/flex';

// Not the shared intermediaryInformationCards: a broker must not see the wholesale comment card.
const intermediaryFields = [
  {
    accessorKey: 'companyInfo.name',
    key: 'name',
    type: 'text',
  },
  {
    accessorKey: 'customFields.email',
    key: 'email',
    type: 'text',
    allowEdition: true,
  },
  {
    accessorKey: 'customFields.phoneNumber',
    key: 'phoneNumber',
    type: 'text',
    allowEdition: true,
  },
  {
    accessorKey: 'companyInfo.location.address',
    key: 'address',
    type: 'text',
  },
  {
    accessorKey: 'companyInfo.location.postCode',
    key: 'postCode',
    type: 'postcode',
    countries: ['FR'],
  },
  {
    accessorKey: 'companyInfo.location.city',
    key: 'city',
    type: 'text',
  },
  {
    accessorKey: 'companyInfo.siret',
    key: 'siret',
    type: 'text',
    allowEdition: true,
  },
  {
    accessorKey: 'companyInfo.nafCode',
    key: 'nafCode',
    type: 'text',
  },
  {
    accessorKey: 'customFields.orias',
    key: 'orias',
    type: 'text',
    allowEdition: true,
  },
  {
    accessorKey: 'customFields.employeeCount',
    key: 'employeeCount',
    type: 'number',
    allowEdition: true,
  },
  {
    accessorKey: 'customFields.ceoFirstName',
    key: 'ceoFirstName',
    type: 'text',
    allowEdition: true,
  },
] as const as IntermediaryInformationCardModuleField[];

const contactFields = [
  {
    accessorKey: 'customFields.contactLastName',
    key: 'contactLastName',
    type: 'text',
    allowEdition: true,
  },
  {
    accessorKey: 'customFields.contactFirstName',
    key: 'contactFirstName',
    type: 'text',
    allowEdition: true,
  },
  {
    accessorKey: 'customFields.contactEmail',
    key: 'contactEmail',
    type: 'text',
    allowEdition: true,
  },
  {
    accessorKey: 'customFields.contactPhoneNumber',
    key: 'contactPhoneNumber',
    type: 'text',
    allowEdition: true,
  },
] as const as IntermediaryInformationCardModuleField[];

const bankingFields = [
  {
    key: 'iban',
    accessorKey: 'customFields.iban',
    type: 'text',
    allowEdition: true,
  },
] as const as IntermediaryInformationCardModuleField[];

export function MyBrokerageFirm() {
  const { t } = useTranslation();

  const { data: user } = useSuspenseQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const brokerageFirmId = user.brokerageFirmId!;

  const {
    data: { firmType },
  } = useSuspenseQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId),
  });

  const { data: brokerageFirmDocumentsList } = useQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
    select: data => data.brokerageFirmDocuments?.brokerageFirmDocumentsList,
  });

  return (
    <Stack>
      <IntermediaryInformationCardModule
        titleKey="companyInformation"
        fields={intermediaryFields}
        brokerageFirmId={brokerageFirmId}
      />

      <IntermediaryInformationCardModule
        titleKey="contactInformation"
        fields={contactFields}
        brokerageFirmId={brokerageFirmId}
      />

      <IntermediaryInformationCardModule
        titleKey="bankingInformation"
        fields={bankingFields}
        brokerageFirmId={brokerageFirmId}
      />

      {firmType === 'RETAIL' && (
        <DocumentsListBrokerageFirmModule
          brokerageFirmId={brokerageFirmId}
          documentsList={[
            { category: 'DISTRIBUTION_CONTRACT' },
            ...(brokerageFirmDocumentsList ?? []),
          ]}
          title={t('intermediaryInformationCard.titles.supportingDocuments')}
        />
      )}
    </Stack>
  );
}
